// COMP-BODY-MAP (US-CLN-070، 111، 112): نقشه صورت و بدن (جلو و پشت) با سه کاربرد: انتخاب نواحی جلسه یا تصویر، ثبت نقطه تزریق روی صورت با مختصات
// نرمال (۰ تا ۱) و نمای رنگی نواحی لیزر بر اساس تعداد جلسات. صفحه‌کلید: هر ناحیه Tab و Enter/Space؛ فهرست Chip نواحی هم برای دسترس‌پذیری.
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@phoenix/i18n';
import { faDigits } from '@phoenix/ui';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { heatLevel, normalizePoint, OUTLINES, REGIONS, VIEW_BOX, type BodyMapView, type BodyRegion, type RegionShape } from './bodyRegions';

export interface BodyMapPoint {
  x: number;
  y: number;
  label?: string;
}

export interface BodyMapProps {
  /** نماهای قابل نمایش (پیش‌فرض: صورت، جلو و پشت). */
  views?: BodyMapView[];
  /** نواحی انتخاب‌شده. */
  selected?: readonly string[];
  /** تغییر انتخاب (بدون آن فقط نمایش). */
  onToggle?: (code: string) => void;
  /** تعداد جلسات هر ناحیه (رنگ — US-CLN-112). */
  counts?: Readonly<Record<string, number>>;
  /** نقاط تزریق (فقط روی نمای صورت یا بدن جاری). */
  points?: readonly BodyMapPoint[];
  /** افزودن نقطه با کلیک (US-CLN-111). */
  onPoint?: (point: { x: number; y: number; area: string | null; view: BodyMapView }) => void;
  /** نمای نقطه‌گذاری. */
  pointView?: BodyMapView;
  /** شناسه آزمون. */
  testId?: string;
}

function Shape({ shape, ...rest }: { shape: RegionShape } & React.SVGProps<SVGElement>) {
  switch (shape.kind) {
    case 'ellipse':
      return <ellipse cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} {...(rest as React.SVGProps<SVGEllipseElement>)} />;
    case 'rect':
      return <rect x={shape.x} y={shape.y} width={shape.width} height={shape.height} rx={shape.r ?? 0} {...(rest as React.SVGProps<SVGRectElement>)} />;
    default:
      return <path d={shape.d} {...(rest as React.SVGProps<SVGPathElement>)} />;
  }
}

export function BodyMap({ views = ['face', 'front', 'back'], selected = [], onToggle, counts, points, onPoint, pointView, testId = 'body-map' }: BodyMapProps) {
  const { t } = useTranslation('cln');
  const theme = useTheme();
  const [view, setView] = useState<BodyMapView>(pointView ?? views[0] ?? 'face');
  const max = counts ? Math.max(0, ...Object.values(counts)) : 0;
  const heat = [
    'transparent',
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
    theme.palette.secondary.dark,
  ];
  const size = VIEW_BOX[view];
  const regions = REGIONS.filter((region) => region.view === view);
  const interactive = Boolean(onToggle) && !onPoint;

  const fillOf = (region: BodyRegion) => {
    if (counts) {
      const level = heatLevel(counts[region.code] ?? 0, max);
      return level === 0 ? theme.palette.action.hover : heat[level];
    }
    return selected.includes(region.code) ? theme.palette.primary.main : theme.palette.action.hover;
  };

  const click = (event: MouseEvent<SVGSVGElement>) => {
    if (!onPoint) {
      return;
    }
    const target = event.target as Element;
    const area = target.getAttribute('data-area');
    onPoint({ ...normalizePoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect()), area, view });
  };

  const key = (event: KeyboardEvent<SVGElement>, code: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle?.(code);
    }
  };

  return (
    <Stack spacing={1} data-testid={testId} data-screen-id="COMP-BODY-MAP">
      {views.length > 1 && (
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_event, value: BodyMapView | null) => value && setView(value)} aria-label={t('cln:body_map.view')}>
          {views.map((option) => (
            <ToggleButton key={option} value={option} data-testid={`${testId}-view-${option}`}>
              {t(`cln:body_map.views.${option}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <Box sx={{ maxWidth: view === 'face' ? 280 : 220, mx: 'auto', width: '100%' }}>
        <svg
          viewBox={`0 0 ${size.width} ${size.height}`}
          role="group"
          aria-label={t(`cln:body_map.views.${view}`)}
          style={{ width: '100%', height: 'auto', cursor: onPoint ? 'crosshair' : 'default', touchAction: 'manipulation' }}
          onClick={click}
          data-testid={`${testId}-svg`}
          data-view={view}
        >
          {OUTLINES[view].map((shape, index) => (
            <Shape key={`outline-${index}`} shape={shape} fill={theme.palette.background.paper} stroke={theme.palette.divider} strokeWidth={1.5} />
          ))}
          {regions.map((region) =>
            region.shapes.map((shape, index) => (
              <Shape
                key={`${region.code}-${index}`}
                shape={shape}
                data-area={region.code}
                data-testid={index === 0 ? `${testId}-area-${region.code}` : undefined}
                fill={fillOf(region)}
                fillOpacity={region.code === 'full_face' ? 0.35 : 0.85}
                stroke={selected.includes(region.code) ? theme.palette.primary.dark : theme.palette.divider}
                strokeWidth={selected.includes(region.code) ? 2 : 1}
                role={interactive ? 'checkbox' : undefined}
                aria-checked={interactive ? selected.includes(region.code) : undefined}
                aria-label={t(`cln:body_area.${region.code}`)}
                tabIndex={interactive && index === 0 ? 0 : undefined}
                onClick={interactive ? () => onToggle?.(region.code) : undefined}
                onKeyDown={interactive ? (event) => key(event, region.code) : undefined}
                style={{ cursor: interactive || onPoint ? 'pointer' : 'default' }}
              >
                <title>
                  {t(`cln:body_area.${region.code}`)}
                  {counts ? ` · ${faDigits(counts[region.code] ?? 0)}` : ''}
                </title>
              </Shape>
            )),
          )}
          {(points ?? []).map((point, index) => (
            <g key={`point-${index}`} pointerEvents="none">
              <circle cx={point.x * size.width} cy={point.y * size.height} r={6} fill={theme.palette.error.main} stroke={theme.palette.background.paper} strokeWidth={1.5} />
              <text x={point.x * size.width} y={point.y * size.height + 3} textAnchor="middle" fontSize={8} fill={theme.palette.error.contrastText}>
                {point.label ?? faDigits(index + 1)}
              </text>
            </g>
          ))}
        </svg>
      </Box>
      {onToggle && !onPoint && (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" aria-label={t('cln:body_map.areas')}>
          {regions.map((region) => (
            <Chip
              key={region.code}
              size="small"
              label={t(`cln:body_area.${region.code}`)}
              color={selected.includes(region.code) ? 'primary' : 'default'}
              variant={selected.includes(region.code) ? 'filled' : 'outlined'}
              onClick={() => onToggle(region.code)}
              data-testid={`${testId}-chip-${region.code}`}
            />
          ))}
        </Stack>
      )}
      {counts && (
        <Typography variant="caption" color="text.secondary">
          {t('cln:body_map.legend')}
        </Typography>
      )}
    </Stack>
  );
}
